import { FbTestingPageComponent } from '../fb-testing-page/fb-testing-page.component';
import { AppSettings } from './app-settings';
import * as Sentry from '@sentry/browser';
import {HttpErrorResponse} from '@angular/common/http';
import {FacebookAuthPreviousState, FacebookAuthSource} from '../app.constants';
import { ServiceLocator } from '../service-locator';
import { Router } from '@angular/router';

export class WebUtils {
  /**
   * a helper to extract the active account id in current url
   * @returns {number}
   */
  public static extractActiveAccountFromUrl(): number {
    const url = window.location.href;
    const regex = new RegExp('[?&]activeAccount(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);

    if (results && results[2]) {
      return Number(results[2]);
    } else {
      return undefined;
    }
  }

  public static validateFreeEmailProvider(email): boolean {
    if (!email) {
      return false;
    }
    const emailRegex = '^.+@(gmail|yahoo|hotmail|aol|msn|yahoo|wanadoo|orange|comcast|live|rediffmail|free|gmx\\.de|web\\.de|yandex|ymail|libero|outlook|uol\\.com\\.br|bol\\.com\\.br|mail\\.ru|cox\\.net|sbcglobal\\.net|sfr\\.fr|verizon|googlemail|ig\\.com\\.br|bigpond\\.com|terra\\.com\\.br|neuf\\.fr|alice\\.it|rocketmail|att\\.net|laposte\\.net|facebook|bellsouth\\.net|charter\\.net|rambler\\.ru|tiscali\\.it|shaw\\.ca|sky\\.com|earthlink\\.net|optonline\\.net|freenet|t-online|aliceadsl\\.fr|virgilio\\.it|home\\.nl|qq\\.com|telenet\\.be|me\\.com|tiscali\\.co\\.uk|voila\\.fr|gmx\\.net|mail\\.com|planet\\.nl|tin\\.it|ntlworld\\.com|arcor\\.de|frontiernet\\.net|hetnet\\.nl|live\\.com\\.au|yahoo\\.com\\.sg|zonnet\\.nl|club-internet\\.fr|juno\\.com|optusnet\\.com\\.au|blueyonder\\.co\\.uk|bluewin\\.ch|skynet\\.be|sympatico\\.ca|windstream\\.net|mac\\.com|centurytel\\.net|chello\\.nl|live\\.ca|aim\\.com|bigpond\\.net\\.au).*$';
    return email.match(emailRegex);
  }

  public static igChannelAuthorizationLink(channelId, influencerId, handle, token, page: string) {
    const fbInfo = AppSettings.getFBInfo();
    const source = WebUtils.extractSourceFromActivePage(page);
    const state = `${fbInfo.fbAppState};${page};channel_id=${channelId};influencer_id=${influencerId};handle=${handle};authorizationToken=${token};source=${source}`;
    const scopes = AppSettings.facebookAuthScopes();
    const facebookAuthUrl = `${FbTestingPageComponent.FB_GRAPH_API}/dialog/oauth?client_id=${fbInfo.facebookAppId}&redirect_uri=${fbInfo.fbChannelAuthorizeUri}&state=${state}&response_type=code&scope=${scopes}`;
    return facebookAuthUrl;
  }

  private static extractSourceFromActivePage(activePage) {
    if (activePage === FacebookAuthPreviousState.AUTHENTICATED) {
      return FacebookAuthSource.INFLUENCER_SETTING;
    } else if (activePage === FacebookAuthPreviousState.ON_CAMPAIGN_INVITATION) {
      return FacebookAuthSource.CAMPAIGN_INVITATION;
    } else if (activePage === FacebookAuthPreviousState.ON_CONTACT_PERMISSION) {
      return FacebookAuthSource.CONTACT_PERMISSION;
    } else {
      return null;
    }
  }

  public static validateEmail(email): boolean {
    if (!email) {
      return false;
    }
    return email.indexOf('@') >= 0;
  }

  public static extractQueryParams(name) {
    const results = new RegExp('[\?&]' + name + '=([^&#]*)').exec(window.location.href);
    if (results == null) {
      return null;
    }
    return decodeURI(results[1]);
  }

  public static reportErrorToSentry(message, error) {
    if (error && error instanceof HttpErrorResponse) {
      const innerErrorData = error.error && error.error.data;

      switch (error.status) {
        case 403:
          if (innerErrorData === 'no_account_level_assigned'
            || innerErrorData === 'no_monthly_report_usage') {
            Sentry.captureException(new Error(`${message}: ${error.error.message}`));
          }
          break;

        default:
          if (error.status < 400 || error.status >= 500) {
            Sentry.captureException(new Error(message));
          }
          break;
      }
   } else {
      Sentry.captureException(new Error(message));
   }
  }

  public static getTargetUrl() {
    let targetUrl = WebUtils.extractQueryParams('target_url');
    if (!targetUrl) {
      targetUrl = encodeURIComponent(window.location.href.split('#')[1]);
    }
    return targetUrl;
  }

  public static isRequestAuthorizedInfluencerAppPage() {
    return window.location.href.indexOf('/#/dashboard') >= 0;
  }

  public static navigateToInactivePageIfNeeded(companyId: number): void {
    const isInAccountSettingPage = window.location.href.indexOf('/#/account-settings') >= 0;

    if (isInAccountSettingPage) { return; }

    const router = ServiceLocator.injector.get(Router);
    router.navigate(['/inactive-account'], { queryParams: { activeAccount: companyId } });
  }

  public static getOutOfInactiveStateIfNeeded(): void {
    const isInInactiveState = window.location.href.indexOf('/#/inactive-account') >= 0;
    if (!isInInactiveState) { return; }

    const router = ServiceLocator.injector.get(Router);
    router.navigateByUrl('/');
  }

  public static navigateToMmiLoginPage(): void {
    window.location.href = AppSettings.mmiLoginLink();
  }

  public static navigateToMmiLogoutPage(): void {
    window.location.href = AppSettings.mmiLogoutLink();
  }

  public static buildSocialChannelLink(platform: { name: string, domain: string }, handle: string): string {
    const platformName = platform.name.toLowerCase();

    switch (platformName) {
      case 'instagram':
      case 'facebook':
      case 'twitter':
      case 'pinterest':
        return `https://${platform.domain}${handle}`;

      case 'tiktok':
        return `https://${platform.domain}@${handle}`;

      case 'youtube':
        return `https://${platform.domain}channel/${handle}`;

      case 'blogs':
        return `https://${handle}`;

      case 'snapchat':
        return `https://${platform.domain}add/@${handle}`;

      default:
        return null;
    }
  }

  public static loadHubspotChatWidget() {
    const hubspotConversations = (<any>window).HubSpotConversations;

    if (hubspotConversations && hubspotConversations.widget) {
      const status = hubspotConversations.widget.status();

      if (status.loaded) {
        return;
      }

      hubspotConversations.widget.load();
    }
  }

  public static removeHubspotChatWidget() {
    const hubspotConversations = (<any>window).HubSpotConversations;

    if (hubspotConversations && hubspotConversations.widget) {
      const status = hubspotConversations.widget.remove();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/utils/web.utils.ts